// The issue (Console D, views/triage.js #/issues/:id): the symbol and where it crashes as a mono
// title, its kind, id, state and first release in the meta, the actions (resolve, resolve in a
// release, ignore, assign, reopen; D100) and where it stands, a numbers strip, then its
// occurrences a day by release (APS-298), the stack with the crashing in-app frame as the one
// accent, the trail before it, what its sessions share, and its sessions, devices and releases.
// The side holds the latest session and the grouping, with the fingerprints merged into it
// (D102). ‹ › and j/k go through the list it was opened from; Esc goes back to it.
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import {
  fetchSessions,
  issueQuery,
  issueSeriesQuery,
  issuesQuery,
  membersQuery,
} from '../api/queries'
import { CohortPanel } from '../components/CohortPanel'
import {
  Btn,
  copyText,
  EmptyNote,
  PageHead,
  Section,
  StackBars,
  Strip,
  TabCount,
  TableFoot,
  Tag,
  useToast,
} from '../components/d'
import { FlagsPanel } from '../components/FlagsPanel'
import { AgentHandoffPanel, HandOffAction, LinkedPanel } from '../components/IssueHandoff'
import { MergedIssues } from '../components/IssueMerges'
import { IssueActions, membersOf, stateLine } from '../components/IssueStates'
import { KindPill, StatusGlyph } from '../components/IssueTable'
import { Icon } from '../components/icons'
import { deviceMeta } from '../components/Perf'
import { SessionPath } from '../components/SessionPath'
import { ErrorState, Loading } from '../components/states'
import { useI18n } from '../i18n'
import { duration, offset, shortId } from '../lib/format'
import { issueOrder } from '../lib/issueOrder'
import { breakdowns, glyphOf, issueTitle, stackOf } from '../lib/issues'
import { shortcutOff } from '../lib/keys'
import { parseTime } from '../lib/session'
import { sandboxFor } from '../sandbox'
import { useProjectParams } from './params'
import { Outcome } from './Sessions'
import { carried, deviceParams, type ScopeSearch } from './search'

export const SYMBOLS_DOCS = 'https://docs.apsio.io/symbols/upload'

/** The days of the occurrences chart. */
const CHART_DAYS = 14
/** Sessions listed under the tabs. */
const SESSIONS_SHOWN = 8

type DetailTab = 'sessions' | 'devices' | 'releases'

export function IssuePage() {
  const i18n = useI18n()
  const { t } = i18n
  const { orgId, projectId } = useProjectParams()
  const { issueId } = useParams({ strict: false }) as { issueId: string }
  const search = useSearch({ strict: false }) as ScopeSearch
  const scope = carried(search)
  const issue = useQuery(issueQuery(projectId, issueId))
  const crumb = (
    <>
      <Link to="/o/$orgId/p/$projectId/issues" params={{ orgId, projectId }} search={scope}>
        {t('nav.issues')}
      </Link>
      {' / '}
      <span translate="no">{shortId(issueId, 8, 4)}</span>
    </>
  )

  if (issue.isPending)
    return (
      <main id="main" className="pg">
        <nav className="pg-crumb" aria-label={t('issue.breadcrumb')}>
          {crumb}
        </nav>
        <Loading />
      </main>
    )
  if (issue.isError)
    return (
      <main id="main" className="pg">
        <PageHead crumb={crumb} title={t('issue.title')} />
        <ErrorState error={issue.error} retry={() => issue.refetch()} />
      </main>
    )
  return <IssueView crumb={crumb} />
}

function IssueView({ crumb }: { crumb: ReactNode }) {
  const i18n = useI18n()
  const { t, number, relative, date } = i18n
  const { orgId, projectId } = useProjectParams()
  const { issueId } = useParams({ strict: false }) as { issueId: string }
  const search = useSearch({ strict: false }) as ScopeSearch
  const navigate = useNavigate()
  const scope = carried(search)
  // An issue's page is by device already: a carried device filter is said not to apply (APS-228).
  const notApplied = deviceMeta(i18n, undefined, deviceParams(search), 'na')
  const issue = useQuery(issueQuery(projectId, issueId))
  // The sandbox is read-only (D64): no members to assign, no changes.
  const sandbox = !!sandboxFor(projectId)
  const members = useQuery({ ...membersQuery(orgId), enabled: !sandbox })
  const d = issue.data
  const s = d?.issue
  const series = useQuery({
    ...issueSeriesQuery(projectId, issueId, { app_id: s?.app_id, days: CHART_DAYS }),
    enabled: !!s,
  })
  const sessions = useQuery({
    queryKey: ['project', projectId, 'issue', issueId, 'sessions', s?.app_id],
    queryFn: () =>
      fetchSessions(projectId, {
        issue_id: issueId,
        app_id: s?.app_id,
        days: 30,
        limit: SESSIONS_SHOWN,
        // The issue whole: its sessions of every build, as its detail is (APS-508).
        build: 'all',
      }),
    enabled: !!s,
  })
  const latestRelease = d?.latest.release
  // Its rank among the release's issues by sessions, derived from the list (APS-308).
  const rank = useQuery({
    ...issuesQuery(projectId, {
      app_id: s?.app_id,
      release: latestRelease,
      sort: 'sessions',
      state: 'all',
      days: 30,
      limit: 200,
      build: 'all',
    }),
    enabled: !!latestRelease,
  })
  const [showSystem, setShowSystem] = useState(false)
  const [tab, setTab] = useState<DetailTab>('sessions')
  const { toast, show } = useToast()

  // ‹ › and j/k through the list it was opened from, else the list's default view (Unresolved,
  // by users, in the bar's scope); Esc back to the list.
  const stored = issueOrder(projectId)
  const fromList = stored.includes(issueId)
  const defaults = useQuery({
    ...issuesQuery(projectId, {
      app_id: search.app,
      release: search.release,
      // The bar's builds, as the list it would have come from (APS-508).
      ...(search.build ? { build: search.build } : {}),
      state: 'unresolved',
      sort: 'users',
      days: 7,
      limit: 50,
    }),
    enabled: !fromList,
  })
  const order = fromList ? stored : (defaults.data?.issues.map((i) => i.id) ?? [])
  const at = order.indexOf(issueId)
  const prev = at > 0 ? order[at - 1] : undefined
  const next = at >= 0 ? order[at + 1] : undefined
  const keys = useRef<(e: KeyboardEvent) => void>(() => {})
  keys.current = (e) => {
    if (shortcutOff(e)) return
    const go = (id: string | undefined) => {
      if (!id) return
      e.preventDefault()
      navigate({
        to: '/o/$orgId/p/$projectId/issues/$issueId',
        params: { orgId, projectId, issueId: id },
        search: scope,
      })
    }
    if (e.key === 'j') go(next)
    else if (e.key === 'k') go(prev)
    else if (e.key === 'Escape') {
      e.preventDefault()
      navigate({ to: '/o/$orgId/p/$projectId/issues', params: { orgId, projectId }, search: scope })
    }
  }
  useEffect(() => {
    const listener = (e: KeyboardEvent) => keys.current(e)
    document.addEventListener('keydown', listener)
    return () => document.removeEventListener('keydown', listener)
  }, [])

  if (!d || !s) return null
  const latest = d.latest
  const stack = stackOf(latest)
  const crashing = stack.frames[stack.crashing]
  const visible = stack.frames.filter((f) => showSystem || !f.system || f === crashing)
  const hidden = stack.frames.length - visible.length
  const { devices, os } = breakdowns(d)
  const firstRelease = [...d.releases].sort((a, b) => a.first_seen.localeCompare(b.first_seen))[0]
  const latestAt = parseTime(latest.ts)
  const title = crashing
    ? t('issue.headline', { type: issueTitle(s), where: crashing.where || crashing.symbol })
    : issueTitle(s)
  const totalOccurrences = d.releases.reduce((n, r) => n + r.occurrences, 0)
  const team = membersOf(members.data)
  const glyph = glyphOf(s, undefined)
  const missingSymbols = !latest.symbolicated && s.kind !== 'abnormal_exit'
  const ranked = rank.data?.issues ?? []
  const place = ranked.findIndex((i) => i.id === s.id)
  const sinceRelease = s.state?.regressed_release ?? firstRelease?.release ?? s.first_seen_release
  const lines = stateLine(i18n, s.state, team)
  const lastSeen = i18n.relativeParts(s.last_seen)
  const plainState =
    (!s.state || (s.state.status === 'unresolved' && !s.state.regressed_release)) &&
    lines.length <= 1
  const plainStack = stack.frames.map((f) => `${f.index} ${f.symbol} ${f.where}`.trim()).join('\n')
  const agentText = [
    t('issue.agent.head', { id: s.id, project: projectId }),
    t('issue.agent.mcp', { id: s.id, project: projectId }),
    `apsio issue ${s.id}`,
  ].join('\n')
  const releasesSeen = d.releases.filter((r) => r.occurrences > 0).length

  return (
    <main id="main" className="pg">
      <div className="is-head">
        <div className="is-top">
          <nav className="pg-crumb" aria-label={t('issue.breadcrumb')}>
            {crumb}
          </nav>
          {at >= 0 ? (
            <nav
              className="is-pager"
              aria-label={t('issue.pager', { n: at + 1, total: order.length })}
            >
              <Pager
                id={prev}
                label={t('issue.pager.prev')}
                glyph="‹"
                orgId={orgId}
                projectId={projectId}
                scope={scope}
              />
              <span aria-hidden="true">
                {t('issue.pager.of', { n: at + 1, total: order.length })}
              </span>
              <Pager
                id={next}
                label={t('issue.pager.next')}
                glyph="›"
                orgId={orgId}
                projectId={projectId}
                scope={scope}
              />
            </nav>
          ) : null}
        </div>
        <PageHead
          title={<span translate="no">{title}</span>}
          variant="mono"
          meta={
            <>
              <KindPill kind={s.kind} />
              {' · '}
              <span translate="no">{shortId(s.id, 8, 4)}</span>
              {' · '}
              <StatusGlyph glyph={glyph} release={s.state?.regressed_release} />
              {sinceRelease ? (
                <>
                  {' · '}
                  <span translate="no">{t('issue.sinceShort', { release: sinceRelease })}</span>
                </>
              ) : null}
              {missingSymbols ? (
                <>
                  {' · '}
                  <span className="c-warn">{t('issue.symbols.none')}</span>
                </>
              ) : null}
              {notApplied ? (
                <>
                  {' · '}
                  {notApplied}
                </>
              ) : null}
              <span className="is-msg" translate="no">
                {latest.message}
              </span>
            </>
          }
        />
        {sandbox ? null : (
          <IssueActions
            orgId={orgId}
            projectId={projectId}
            issueId={s.id}
            appId={s.app_id}
            state={s.state}
            members={team}
            extra={
              <>
                <span className="is-handoff">
                  <HandOffAction
                    orgId={orgId}
                    projectId={projectId}
                    issueId={s.id}
                    notify={show}
                    primary={!s.state || s.state.status === 'unresolved'}
                  />
                </span>
                <Btn
                  variant="ghost"
                  onClick={async () =>
                    show((await copyText(agentText)) ? t('issues.copied') : t('issues.copyFailed'))
                  }
                >
                  <Icon name="copy" />
                  {t('issues.copyForAgent')}
                </Btn>
              </>
            }
          />
        )}
        {/* One status, in the meta; this line says only what it adds (resolved in, ignored
            until, assigned), and stays as a live region for the changes made here. */}
        <p className="is-reo" role="status">
          {plainState
            ? null
            : lines.map((l, n) =>
                // A resolution is the status select's value and the meta's word: said to a screen
                // reader here, not shown a third time (Mosaic, APS-390).
                n === 0 && s.state?.status === 'resolved' ? (
                  <span key={l} className="vh">
                    {l}
                  </span>
                ) : (
                  <span key={l}>
                    {n > (s.state?.status === 'resolved' ? 1 : 0) ? ' · ' : ''}
                    {l}
                  </span>
                ),
              )}
          {(d.fix_observed ?? []).map((f) => (
            <span key={f.app_id} title={t('issue.fixObservedHint')} className="faint">
              {t('issue.fixObserved', { release: f.release, sessions: f.sessions })}
            </span>
          ))}
        </p>
      </div>

      <Strip
        label={t('issue.numbers')}
        items={[
          {
            dt: t('issue.sessions'),
            dd: number(s.sessions),
            d: t('issue.occurrencesCount', { count: s.occurrences }),
          },
          { dt: t('issue.users'), dd: number(s.users) },
          ...(latestRelease && place >= 0
            ? [
                {
                  dt: t('issue.rank', { release: latestRelease }),
                  dd: `${place + 1}`,
                  small: t('issue.rankOf', { total: ranked.length }),
                  d: t('issue.rankBy'),
                },
              ]
            : []),
          {
            dt: s.state?.regressed_release ? t('issue.regressedIn') : t('issue.firstSeen'),
            dd: <span translate="no">{sinceRelease ?? date(s.first_seen)}</span>,
            d: firstRelease ? date(firstRelease.first_seen) : date(s.first_seen),
          },
          {
            dt: t('issue.lastSeenLabel'),
            dd: lastSeen[0],
            small: lastSeen[1] || undefined,
            d: (
              <span translate="no">
                {[latest.device_model, `${latest.os_name} ${latest.os_version}`.trim()]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            ),
          },
        ]}
      />

      <div className="grid2">
        <div className="sections">
          <Occurrences
            series={series.data}
            loading={series.isPending}
            release={latestRelease}
            label={t('issue.chart.label', { id: s.id, days: CHART_DAYS })}
          />

          <Section
            title={t('issue.stack')}
            meta={
              stack.source === 'symbolicated' ? t('issue.stack.symbolicated') : t('issue.stack.raw')
            }
          >
            {stack.frames.length === 0 ? (
              <EmptyNote title={t('issue.stack.empty')}>{t('issue.stack.emptyBody')}</EmptyNote>
            ) : (
              <div className="win is-stack">
                <div className="win-bar">
                  <span className="is-wt" translate="no">
                    <b>{s.kind}</b> · {latest.type || s.type}
                    {latest.phase === 'launch' ? (
                      <>
                        {' '}
                        <Tag title={t('issue.latest.launch.title')}>
                          <span translate="yes">{t('issue.latest.launch')}</span>
                        </Tag>
                      </>
                    ) : null}
                  </span>
                  <span className="r">
                    {stack.systemCount > 0 ? (
                      <Btn
                        size="sm"
                        variant="ghost"
                        aria-expanded={showSystem}
                        onClick={() => setShowSystem(!showSystem)}
                      >
                        {showSystem
                          ? t('issue.stack.hideSystem')
                          : t('issue.stack.showSystem', { count: hidden })}
                      </Btn>
                    ) : null}
                    <Btn
                      size="sm"
                      variant="ghost"
                      onClick={async () =>
                        show(
                          (await copyText(plainStack))
                            ? t('issue.stack.copied')
                            : t('issues.copyFailed'),
                        )
                      }
                    >
                      <Icon name="copy" small />
                      {t('issue.stack.copy')}
                    </Btn>
                  </span>
                </div>
                <ol className="frames" translate="no">
                  {visible.map((f) => (
                    <li
                      key={`${f.index}-${f.symbol}`}
                      className={[
                        f === crashing ? 'cur' : '',
                        f.system ? 'sys' : '',
                        stack.source === 'raw' ? 'raw' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      aria-current={f === crashing ? 'true' : undefined}
                    >
                      <span className="no">{f.index}</span>
                      <span>
                        <b>{f.symbol}</b>
                        <span className="is-where" title={f.path}>
                          {f.where}
                        </span>
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
            {missingSymbols ? (
              <div className="is-stack-foot">
                <span className="note">
                  <span className="c-warn">!</span> <b>{t('issue.symbols.missing')}</b>{' '}
                  {t('issue.symbols.missingBody', {
                    count: latest.missing_debug_ids.length,
                    build: latest.build || latest.release,
                  })}
                </span>
                <a className="ul" href={SYMBOLS_DOCS} target="_blank" rel="noreferrer">
                  {t('issue.symbols.howTo')}
                </a>
              </div>
            ) : null}
          </Section>

          <Section
            title={t('issue.before')}
            meta={t('issue.before.sub', { session: shortId(latest.session_id) })}
            right={
              <Link
                className="btn ghost sm"
                to="/o/$orgId/p/$projectId/sessions/$sessionId"
                params={{ orgId, projectId, sessionId: latest.session_id }}
                search={scope}
              >
                {t('issue.openTheSession')}
              </Link>
            }
          >
            <ol className="trail is-trail" translate="no">
              {d.breadcrumbs.map((b, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: breadcrumbs have no id
                <li key={i}>
                  <span className="dt">{offset(i18n, parseTime(b.ts) - latestAt)}</span>
                  <span className="is-g" aria-hidden="true">
                    ·
                  </span>
                  <span className="wt">
                    <b>{b.category}</b> <span className="faint">{b.message}</span>
                  </span>
                  <span />
                </li>
              ))}
              <li className="is-at" aria-current="true">
                <span className="dt">{offset(i18n, 0)}</span>
                <span className="is-g" aria-hidden="true">
                  ●
                </span>
                <span className="wt">
                  <b>{s.kind}</b> <span className="faint">{latest.type || s.type}</span>
                  {latest.phase === 'launch' ? (
                    <>
                      {' '}
                      <Tag title={t('issue.latest.launch.title')}>
                        <span translate="yes">{t('issue.latest.launch')}</span>
                      </Tag>
                    </>
                  ) : null}
                </span>
                <span />
              </li>
            </ol>
            {d.breadcrumbs.length === 0 ? <p className="note">{t('issue.before.none')}</p> : null}
          </Section>

          <CohortPanel projectId={projectId} issueId={issueId} appId={s.app_id} />
          <FlagsPanel orgId={orgId} projectId={projectId} issueId={issueId} appId={s.app_id} />

          <section className="sec" aria-label={t('issue.details')}>
            <div className="tabs" role="tablist" aria-label={t('issue.details')}>
              {(
                [
                  ['sessions', t('issue.sessions'), s.sessions],
                  ['devices', t('issue.devices'), undefined],
                  ['releases', t('issue.releases'), releasesSeen],
                ] as const
              ).map(([id, label, n]) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  id={`is-tab-${id}`}
                  aria-selected={tab === id}
                  aria-controls={`is-panel-${id}`}
                  onClick={() => setTab(id)}
                >
                  {label}
                  {n !== undefined ? (
                    <>
                      {' '}
                      <TabCount n={number(n)} />
                    </>
                  ) : null}
                </button>
              ))}
            </div>
            <div
              className="is-pn"
              role="tabpanel"
              id={`is-panel-${tab}`}
              aria-labelledby={`is-tab-${tab}`}
            >
              {tab === 'sessions' ? (
                <>
                  {sessions.isPending ? (
                    <Loading />
                  ) : sessions.isError ? (
                    <ErrorState error={sessions.error} retry={() => sessions.refetch()} />
                  ) : (
                    <section className="scroll-x" aria-label={t('issue.sessions')} tabIndex={-1}>
                      <table className="t">
                        <thead>
                          <tr>
                            <th scope="col" className="f">
                              {t('issue.col.session')}
                            </th>
                            <th scope="col">{t('issue.col.started')}</th>
                            <th scope="col" className="num">
                              {t('issue.col.length')}
                            </th>
                            <th scope="col">{t('issue.col.outcome')}</th>
                            <th scope="col">{t('releases.col.release')}</th>
                            <th scope="col">{t('issue.col.device')}</th>
                            <th scope="col" className="last">
                              {t('sessions.col.path')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {sessions.data.sessions.map((x) => (
                            <tr key={x.session_id}>
                              <td className="f">
                                <Link
                                  className="ul"
                                  to="/o/$orgId/p/$projectId/sessions/$sessionId"
                                  params={{ orgId, projectId, sessionId: x.session_id }}
                                  search={scope}
                                  translate="no"
                                >
                                  {shortId(x.session_id)}
                                </Link>
                              </td>
                              <td>{relative(x.started_at)}</td>
                              <td className="num">
                                {duration(
                                  i18n,
                                  Math.max(0, parseTime(x.last_seen_at) - parseTime(x.started_at)),
                                )}
                              </td>
                              <td>
                                <Outcome outcome={x.outcome} />
                              </td>
                              <td translate="no">{x.release}</td>
                              <td className="wrap" translate="no">
                                {x.device_model}
                                <span className="t2">{`${x.os_name} ${x.os_version}`.trim()}</span>
                              </td>
                              <td className="last wrap us-path is-path">
                                <SessionPath screens={x.path.screens} more={x.path.more} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </section>
                  )}
                  <TableFoot>
                    <span>
                      {t('issue.sessionsShown', {
                        count: sessions.data?.sessions.length ?? 0,
                        total: s.sessions,
                      })}
                    </span>
                    <Link
                      className="ul"
                      to="/o/$orgId/p/$projectId/sessions"
                      params={{ orgId, projectId }}
                      search={{ ...scope, issue: issueId, range: '30d' }}
                    >
                      {t('issue.allSessions', { count: s.sessions })}
                    </Link>
                  </TableFoot>
                </>
              ) : tab === 'devices' ? (
                <div className="grid-h" style={{ paddingTop: 8 }}>
                  <ShareList title={t('issue.devices')} meta={t('issue.shareOf')} rows={devices} />
                  <ShareList title={t('issue.os')} meta={t('issue.shareOf')} rows={os} />
                </div>
              ) : (
                <section className="scroll-x" aria-label={t('issue.releases')} tabIndex={-1}>
                  <table className="t">
                    <thead>
                      <tr>
                        <th scope="col" className="f">
                          {t('releases.col.release')}
                        </th>
                        <th scope="col" className="num">
                          {t('issue.occurrences')}
                        </th>
                        <th scope="col" className="num">
                          {t('issue.users')}
                        </th>
                        <th scope="col">{t('issue.firstSeen')}</th>
                        <th scope="col" className="last">
                          {t('issue.lastSeenLabel')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.releases.map((r) => (
                        <tr key={`${r.app_id}-${r.release}`}>
                          <td className="f" translate="no">
                            {r.release}
                          </td>
                          <td className="num">{number(r.occurrences)}</td>
                          <td className="num">{number(r.users)}</td>
                          <td>{date(r.first_seen)}</td>
                          <td className="last">{relative(r.last_seen)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              )}
            </div>
          </section>
        </div>

        <aside className="sections is-side" aria-label={t('issue.context')}>
          {sandbox ? null : (
            <AgentHandoffPanel orgId={orgId} projectId={projectId} issueId={s.id} />
          )}
          <Section
            title={t('issue.latest')}
            meta={t('issue.latest.sub', { count: totalOccurrences })}
          >
            <dl className="kv" translate="no">
              <dt>{t('issue.col.session')}</dt>
              <dd>
                <Link
                  to="/o/$orgId/p/$projectId/sessions/$sessionId"
                  params={{ orgId, projectId, sessionId: latest.session_id }}
                  search={scope}
                >
                  {shortId(latest.session_id)}
                </Link>
              </dd>
              <dt>{t('issue.when')}</dt>
              <dd>{relative(latest.ts)}</dd>
              <dt>{t('issue.col.device')}</dt>
              <dd>
                {[latest.device_model, `${latest.os_name} ${latest.os_version}`.trim()]
                  .filter(Boolean)
                  .join(' · ')}
              </dd>
              <dt>{t('releases.col.release')}</dt>
              <dd>{latest.release}</dd>
            </dl>
            <div className="is-btns">
              <Link
                className="btn sm"
                to="/o/$orgId/p/$projectId/sessions/$sessionId"
                params={{ orgId, projectId, sessionId: latest.session_id }}
                search={scope}
              >
                <Icon name="play" small />
                {t('issue.openSession')}
              </Link>
              <Link
                className="btn ghost sm"
                to="/o/$orgId/p/$projectId/sessions"
                params={{ orgId, projectId }}
                search={{ ...scope, issue: issueId, range: '30d' }}
              >
                {t('issue.allSessionsShort', { count: s.sessions })}
              </Link>
            </div>
          </Section>
          {sandbox ? null : <LinkedPanel orgId={orgId} projectId={projectId} issueId={s.id} />}
          <Section title={t('issue.groupingTitle')} meta={`v${s.grouping.version}`} minor>
            <p className="note">
              {t('issue.grouping', { version: s.grouping.version })}{' '}
              <span translate="no">{s.grouping.reason}</span>
            </p>
          </Section>
          <MergedIssues
            projectId={projectId}
            issueId={s.id}
            fingerprints={s.fingerprints ?? [s.id]}
            mergedFrom={d.merged_from}
            readOnly={sandbox}
          />
        </aside>
      </div>
      {toast}
    </main>
  )
}

function Pager({
  id,
  label,
  glyph,
  orgId,
  projectId,
  scope,
}: {
  id: string | undefined
  label: string
  glyph: string
  orgId: string
  projectId: string
  scope: ScopeSearch
}) {
  return id ? (
    <Link
      className="btn ghost sm"
      to="/o/$orgId/p/$projectId/issues/$issueId"
      params={{ orgId, projectId, issueId: id }}
      search={scope}
      aria-label={label}
    >
      {glyph}
    </Link>
  ) : (
    <span className="btn ghost sm is-off" aria-hidden="true">
      {glyph}
    </span>
  )
}

/** A share list: value, bar, percent (devices and OS versions of the occurrences). */
function ShareList({
  title,
  meta,
  rows,
}: {
  title: string
  meta: string
  rows: { label: string; share: number }[]
}) {
  const { t, percent } = useI18n()
  return (
    <div>
      <Section title={title} meta={meta} minor>
        {rows.map((x) => (
          <div className="share" key={x.label || 'other'}>
            <span translate={x.label ? 'no' : undefined}>{x.label || t('issue.other')}</span>
            <span className="b" aria-hidden="true">
              <i style={{ width: `${Math.max(0, Math.min(100, x.share * 100))}%` }} />
            </span>
            <span>{percent(x.share, 0)}</span>
          </div>
        ))}
      </Section>
    </div>
  )
}

/** Occurrences a day, the latest release over the earlier ones (APS-298). */
function Occurrences({
  series,
  loading,
  release,
  label,
}: {
  series: import('../api/types').IssueSeries | undefined
  loading: boolean
  release: string | undefined
  label: string
}) {
  const { t, date } = useI18n()
  const days = series?.ends.length ?? 0
  const mine = series?.releases?.find((r) => r.release === release)
  const a = series ? (mine?.occurrences ?? series.ends.map(() => 0)) : []
  const b = series ? series.occurrences.map((n, i) => Math.max(0, n - (a[i] ?? 0))) : []
  const shipped = mine?.first_seen
    ? series?.ends.findIndex((end) => end >= (mine.first_seen ?? ''))
    : undefined
  const fmt = (iso: string | undefined) =>
    iso ? date(iso, { month: 'short', day: 'numeric', timeZone: 'UTC' }) : ''
  return (
    <Section
      title={t('issue.occurrencesTitle')}
      meta={t('issue.occurrencesMeta', { days: CHART_DAYS })}
      right={
        release ? (
          <span className="legend" style={{ padding: 0 }} translate="no">
            <span>
              <i />
              {release}
            </span>
            <span>
              <i className="s2" />
              {t('issue.earlierReleases')}
            </span>
          </span>
        ) : null
      }
    >
      {loading ? (
        <Loading />
      ) : !series || days === 0 ? (
        <p className="note">{t('issue.occurrencesNone')}</p>
      ) : (
        <StackBars
          label={label}
          height={150}
          a={a}
          b={b}
          xLabels={[
            [0, fmt(series.ends[0])],
            [days - 1, fmt(series.ends[days - 1])],
          ]}
          {...(release && shipped !== undefined && shipped > 0
            ? { marker: { index: shipped, label: release } }
            : {})}
        />
      )}
    </Section>
  )
}
